import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, Button, Alert, IconButton, Chip, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Avatar, Grid, LinearProgress,
} from '@mui/material';
import { Add, Home, Assessment, Refresh, Logout, Search } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import ExitCard from './ExitCard';
import { isRealDate, diffDays } from '../utils/dateValidation';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const STATUS_COLOR = { Initiated: 'default', ClearanceInProgress: 'info', ClearanceComplete: 'primary', DuesProcessed: 'secondary', Released: 'success', Cancelled: 'error' };
const TYPES = ['Resignation', 'Dismissal', 'Retirement', 'End of Contract', 'Death'];

const emptySep = {
  employeeNo: '', type: 'Resignation', nature: 'Voluntary', noticeDate: '', lastWorkingDay: '',
  noticePeriodDays: '', reason: '', description: '', disciplinaryCaseRef: '',
};

const ExitList = () => {
  const navigate = useNavigate();
  const [seps, setSeps] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError]   = useState('');
  const [openId, setOpenId] = useState(null);
  const [newDlg, setNewDlg] = useState(null);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: Boolean(newDlg), value: newDlg, onClose: () => setNewDlg(null) });
  const [newErr, setNewErr] = useState('');

  const load = useCallback(async () => {
    try {
      const q = new URLSearchParams();
      if (status) q.set('status', status);
      if (type) q.set('type', type);
      const r = await axios.get(`${API}/api/exit/separations?${q}`, { headers: hdrs() });
      setSeps(r.data); setError('');
    } catch (e) { setError(e.response?.data?.error || 'Failed to load separations'); }
  }, [status, type]);
  useEffect(() => { load(); }, [load]);

  const openNew = async () => {
    try { const r = await axios.get(`${API}/api/employees`, { headers: hdrs() }); setEmployees(r.data.filter(e => e.status === 'Active')); }
    catch { setEmployees([]); }
    setNewErr(''); setNewDlg({ ...emptySep });
  };

  const createSep = async () => {
    setNewErr('');
    try {
      const r = await axios.post(`${API}/api/exit/separations`, newDlg, { headers: hdrs() });
      setNewDlg(null); await load(); setOpenId(r.data._id);
    } catch (e) { setNewErr(e.response?.data?.error || 'Could not create separation'); }
  };

  const filtered = seps.filter(s => {
    const q = search.trim().toLowerCase();
    return !q || (s.employeeName || '').toLowerCase().includes(q) || (s.employeeNo || '').toLowerCase().includes(q) || (s.sepNo || '').toLowerCase().includes(q);
  });
  const cleared = (s) => s.clearance.filter(c => c.status === 'Cleared').length;

  // Update a date and auto-recompute the notice period from notice → last working day
  const setDate = (field, value) => {
    const nd = { ...newDlg, [field]: value };
    const np = diffDays(field === 'noticeDate' ? value : nd.noticeDate, field === 'lastWorkingDay' ? value : nd.lastWorkingDay);
    if (np !== '') nd.noticePeriodDays = np;
    setNewDlg(nd);
  };
  const bothDates = !!(newDlg?.noticeDate && newDlg?.lastWorkingDay);
  const noticeInvalid = !!newDlg?.noticeDate && !isRealDate(newDlg.noticeDate);
  const lastInvalid = !!newDlg?.lastWorkingDay && !isRealDate(newDlg.lastWorkingDay);
  const orderErr = bothDates && !noticeInvalid && !lastInvalid && new Date(newDlg.lastWorkingDay) < new Date(newDlg.noticeDate);
  const dateError = noticeInvalid || lastInvalid || orderErr;

  return (
    <Box sx={{ maxWidth: 1150, mx: 'auto', mt: 4, mb: 4, px: 2 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Logout color="primary" />
              <Typography variant="h5" color="primary">Separation &amp; Exit</Typography>
              <Chip label={filtered.length} size="small" color="primary" />
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Reports"><IconButton onClick={() => navigate('/hr/exit/reports')}><Assessment /></IconButton></Tooltip>
              <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
              <Tooltip title="HR Home"><IconButton onClick={() => navigate('/hr')}><Home /></IconButton></Tooltip>
              <Button variant="contained" startIcon={<Add />} onClick={openNew}>New Separation</Button>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box display="flex" gap={1.5} mb={2} flexWrap="wrap">
            <TextField select size="small" label="Status" value={status} onChange={e => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All statuses</MenuItem>
              {Object.keys(STATUS_COLOR).map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Type" value={type} onChange={e => setType(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All types</MenuItem>
              {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField size="small" placeholder="Search employee / ref…" value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }} sx={{ minWidth: 220 }} />
          </Box>

          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell><strong>Employee</strong></TableCell>
              <TableCell><strong>Ref</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Last Day</strong></TableCell>
              <TableCell><strong>Clearance</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
            </TableRow></TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary" py={2}>No separations.</Typography></TableCell></TableRow>
              ) : filtered.map(s => (
                <TableRow key={s._id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpenId(s._id)}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ width: 30, height: 30, bgcolor: '#37474f22', color: '#37474f', fontSize: 13, fontWeight: 700 }}>{(s.employeeName || '?').charAt(0)}</Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600} lineHeight={1.2}>{s.employeeName}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.employeeNo} · {s.department || '—'}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell><Typography variant="caption">{s.sepNo}</Typography></TableCell>
                  <TableCell><Chip label={s.type} size="small" color={s.type === 'Dismissal' ? 'error' : 'default'} variant={s.type === 'Dismissal' ? 'filled' : 'outlined'} /></TableCell>
                  <TableCell>{fmtDate(s.lastWorkingDay)}</TableCell>
                  <TableCell sx={{ minWidth: 130 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LinearProgress variant="determinate" value={(cleared(s) / s.clearance.length) * 100} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                      <Typography variant="caption" color="text.secondary">{cleared(s)}/{s.clearance.length}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell><Chip label={s.status} size="small" color={STATUS_COLOR[s.status] || 'default'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* New separation dialog */}
      {discardDialog}
      <Dialog open={Boolean(newDlg)} onClose={attemptClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Logout color="primary" /> New Separation</DialogTitle>
        <DialogContent dividers>
          {newErr && <Alert severity="error" sx={{ mb: 2 }}>{newErr}</Alert>}
          {newDlg && (
            <Grid container spacing={2.5} sx={{ pt: 1 }}>
              <Grid item xs={12}>
                <TextField select label="Employee *" value={newDlg.employeeNo} onChange={e => setNewDlg({ ...newDlg, employeeNo: e.target.value })} fullWidth>
                  {employees.map(e => <MenuItem key={e.employeeNo} value={e.employeeNo}>{e.employeeNo} — {e.firstName} {e.lastName}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Separation Type *" value={newDlg.type} onChange={e => setNewDlg({ ...newDlg, type: e.target.value, nature: e.target.value === 'Dismissal' ? 'Involuntary' : newDlg.nature })} fullWidth>
                  {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Nature" value={newDlg.nature} onChange={e => setNewDlg({ ...newDlg, nature: e.target.value })} fullWidth>
                  {['Voluntary', 'Involuntary'].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}><TextField type="date" label="Notice Date" InputLabelProps={{ shrink: true }} value={newDlg.noticeDate} onChange={e => setDate('noticeDate', e.target.value)} fullWidth size="small"
                error={noticeInvalid} helperText={noticeInvalid ? 'Not a valid date' : ' '} /></Grid>
              <Grid item xs={12} sm={4}>
                <TextField type="date" label="Last Working Day" InputLabelProps={{ shrink: true }} value={newDlg.lastWorkingDay}
                  onChange={e => setDate('lastWorkingDay', e.target.value)} fullWidth size="small"
                  inputProps={{ min: newDlg.noticeDate || undefined }}
                  error={lastInvalid || orderErr} helperText={lastInvalid ? 'Not a valid date' : orderErr ? 'Cannot be before the notice date' : ' '} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField type="number" label="Notice Period (days)" value={newDlg.noticePeriodDays}
                  onChange={e => setNewDlg({ ...newDlg, noticePeriodDays: e.target.value })} fullWidth size="small"
                  InputProps={{ readOnly: bothDates }}
                  helperText={bothDates ? 'Auto-calculated from dates' : 'Set both dates to auto-calculate'} />
              </Grid>
              <Grid item xs={12}><TextField label="Reason" value={newDlg.reason} onChange={e => setNewDlg({ ...newDlg, reason: e.target.value })} fullWidth size="small" /></Grid>
              <Grid item xs={12}><TextField label="Description" value={newDlg.description} onChange={e => setNewDlg({ ...newDlg, description: e.target.value })} fullWidth size="small" multiline rows={2} /></Grid>
              {newDlg.type === 'Dismissal' && <Grid item xs={12}><TextField label="Disciplinary Case Ref" value={newDlg.disciplinaryCaseRef} onChange={e => setNewDlg({ ...newDlg, disciplinaryCaseRef: e.target.value })} fullWidth size="small" placeholder="e.g. DISC-00001" /></Grid>}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" disabled={!newDlg?.employeeNo || !newDlg?.type || dateError} onClick={createSep}>Create</Button>
        </DialogActions>
      </Dialog>

      {openId && <ExitCard sepId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </Box>
  );
};

export default ExitList;
